import React, { Fragment, useMemo, useState } from "react";

import { ChevronDownIcon, ChevronRightIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Checkbox,
	Flex,
	Spacer,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";

import {
	EXPENSE_CATEGORIES,
	formatAmount,
	getDisplayedMonthNet,
	getRentColorScheme,
	getSavedMonthEntry,
	getUsedExpenseCategories,
	toFiniteNumber,
} from "../../utils/financialStatus";
import { fullDateFormat } from "../../utils/types";

const STATUS_COLOR_SCHEMES = {
	custodie: "purple",
	transferat: "green",
	transferCustodie: "green",
	predareCash: "blue",
	predareCashCustodie: "blue",
	ownerAccount: "teal",
};

/** Value shown for a single property row in a given money column. */
function propertyValue(row, key, currency) {
	if (key === "rent") return row.rent.currency === currency ? toFiniteNumber(row.rent.amount) : 0;
	if (key === "subtotal") return row.rent.currency === currency ? toFiniteNumber(row.subtotal?.[currency]) : 0;
	return toFiniteNumber(row[key]?.[currency]);
}

/** Value shown for a month summary row in a given money column. */
function monthValue(totals, key, currency) {
	if (key === "subtotal") return toFiniteNumber(totals?.net?.[currency]);
	return toFiniteNumber(totals?.[key]?.[currency]);
}

function MoneyText({ value, muteZero = true, colorNegative = false, ...rest }) {
	const zeroColor = useColorModeValue("gray.400", "gray.600");
	const isZero = toFiniteNumber(value) === 0;
	const negative = colorNegative && toFiniteNumber(value) < 0;
	return (
		<Text as="span" color={isZero && muteZero ? zeroColor : negative ? "red.400" : undefined} {...rest}>
			{formatAmount(value)}
		</Text>
	);
}

export default function FinancialStatusTable({ breakdown, year, ownerCurrency, financialStatus, onFinalizeMonth, onPropertyClick }) {
	const months = moment.months();
	const [expandedMonths, setExpandedMonths] = useState(() => new Set());
	const [showBothCurrencies, setShowBothCurrencies] = useState(false);
	const [showAllCategories, setShowAllCategories] = useState(false);
	const [showIdleProperties, setShowIdleProperties] = useState(true);

	const rowBg = useColorModeValue("white", "gray.800");
	const stripeBg = useColorModeValue("gray.50", "whiteAlpha.50");
	const monthBg = useColorModeValue("white", "gray.800");
	const monthHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
	const headBg = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.600");
	const currentMonthRing = useColorModeValue("gray.400", "gray.500");
	const mutedColor = useColorModeValue("gray.500", "gray.400");
	const monthTextColor = useColorModeValue("gray.800", "gray.100");

	const currencies = useMemo(() => {
		if (showBothCurrencies || !ownerCurrency) return ["eur", "ron"];
		return [ownerCurrency];
	}, [showBothCurrencies, ownerCurrency]);

	const shownCategories = useMemo(
		() => (showAllCategories ? EXPENSE_CATEGORIES : getUsedExpenseCategories(breakdown.months)),
		[showAllCategories, breakdown.months],
	);

	const columns = useMemo(() => {
		const list = [{ type: "month" }, { type: "address" }, { type: "money", key: "rent", label: "Rent Received" }, { type: "method" }];
		shownCategories.forEach((category) => list.push({ type: "money", key: category.key, label: category.label }));
		list.push({ type: "money", key: "subtotal", label: "Sub-Total" });
		list.push({ type: "status" });
		return list;
	}, [shownCategories]);

	const hiddenCategoryCount = EXPENSE_CATEGORIES.length - shownCategories.length;
	const currentMonthIndex = year === moment().year() ? moment().month() : -1;

	const toggleMonth = (monthIndex) => {
		setExpandedMonths((previous) => {
			const next = new Set(previous);
			if (next.has(monthIndex)) next.delete(monthIndex);
			else next.add(monthIndex);
			return next;
		});
	};

	const allExpanded = expandedMonths.size === 12;

	const stickyMonth = { position: "sticky", left: 0, zIndex: 1 };
	const stickyAddress = { position: "sticky", left: "5.5rem", zIndex: 1 };

	const renderHeaderCells = () =>
		columns.map((column) => {
			if (column.type === "month")
				return (
					<Th key="month" {...stickyMonth} bg={headBg}>
						Luna
					</Th>
				);
			if (column.type === "address")
				return (
					<Th key="address" {...stickyAddress} bg={headBg}>
						Address
					</Th>
				);
			if (column.type === "method")
				return (
					<Th key="method" bg={headBg}>
						Method
					</Th>
				);
			if (column.type === "status")
				return (
					<Th key="status" bg={headBg}>
						Status
					</Th>
				);
			return (
				<Th key={column.key} colSpan={currencies.length} isNumeric bg={headBg}>
					{column.label}
					{currencies.length === 1 && (
						<Text as="span" fontSize="10px" color={mutedColor} ml={1}>
							{currencies[0]?.toUpperCase()}
						</Text>
					)}
				</Th>
			);
		});

	const renderCurrencyHeaderCells = () =>
		columns.map((column) => {
			if (column.type !== "money")
				return <Th key={`cur-${column.type}`} bg={headBg} {...(column.type === "month" ? stickyMonth : column.type === "address" ? stickyAddress : {})} />;
			return currencies.map((currency) => (
				<Th key={`cur-${column.key}-${currency}`} isNumeric bg={headBg}>
					{currency.toUpperCase()}
				</Th>
			));
		});

	return (
		<Box>
			<Flex mb={3} gap={4} alignItems="center" flexWrap="wrap">
				<Button size="xs" onClick={() => setExpandedMonths(allExpanded ? new Set() : new Set(Array.from({ length: 12 }, (_, i) => i)))}>
					{allExpanded ? "Collapse all months" : "Expand all months"}
				</Button>
				<Spacer />
				{ownerCurrency && (
					<Checkbox size="sm" isChecked={showBothCurrencies} onChange={(e) => setShowBothCurrencies(e.target.checked)}>
						Show both currencies
					</Checkbox>
				)}
				<Checkbox size="sm" isChecked={showAllCategories} onChange={(e) => setShowAllCategories(e.target.checked)}>
					Show empty categories
					{hiddenCategoryCount > 0 && !showAllCategories && (
						<Text as="span" color={mutedColor} ml={1}>
							({hiddenCategoryCount} hidden)
						</Text>
					)}
				</Checkbox>
				<Checkbox size="sm" isChecked={showIdleProperties} onChange={(e) => setShowIdleProperties(e.target.checked)}>
					Show properties without payments
				</Checkbox>
			</Flex>

			<TableContainer maxH="75vh" overflowY="auto" borderWidth="1px" borderRadius="md">
				<Table size="sm" variant="simple">
					<Thead position="sticky" top={0} zIndex={2}>
						<Tr>{renderHeaderCells()}</Tr>
						{currencies.length > 1 && <Tr>{renderCurrencyHeaderCells()}</Tr>}
					</Thead>
					<Tbody>
						{breakdown.months.map((monthData) => {
							const monthIndex = monthData.monthIndex;
							const totals = monthData.totals;
							const saved = getSavedMonthEntry(financialStatus, year, monthIndex);
							const isExpanded = expandedMonths.has(monthIndex);
							const isCurrentMonth = monthIndex === currentMonthIndex;
							const activeRows = monthData.byProperty.filter((row) => row.hasActivity);
							const propertyRows = showIdleProperties ? monthData.byProperty : activeRows;

							return (
								<Fragment key={monthIndex}>
									<Tr
										bg={monthBg}
										color={monthTextColor}
										fontWeight="semibold"
										borderTopWidth="1px"
										borderBottomWidth="1px"
										borderColor={borderColor}
										cursor={onFinalizeMonth ? "pointer" : "default"}
										_hover={onFinalizeMonth ? { bg: monthHoverBg } : undefined}
										onClick={() => onFinalizeMonth?.(monthIndex, totals.net)}
										boxShadow={isCurrentMonth ? `inset 3px 0 0 ${currentMonthRing}` : undefined}
									>
										{columns.map((column) => {
											if (column.type === "month")
												return (
													<Td
														key="month"
														{...stickyMonth}
														bg={monthBg}
														onClick={(event) => {
															event.stopPropagation();
															toggleMonth(monthIndex);
														}}
													>
														<Flex alignItems="center" gap={1}>
															<Box
																as={isExpanded ? ChevronDownIcon : ChevronRightIcon}
																aria-label={isExpanded ? "Collapse month" : "Expand month"}
																cursor="pointer"
																boxSize={4}
															/>
															<Text>{months[monthIndex]}</Text>
														</Flex>
													</Td>
												);
											if (column.type === "address")
												return (
													<Td
														key="address"
														{...stickyAddress}
														bg={monthBg}
														onClick={(event) => {
															event.stopPropagation();
															toggleMonth(monthIndex);
														}}
													>
														<Text fontWeight="normal" fontSize="xs" opacity={0.9}>
															{activeRows.length} / {monthData.byProperty.length} with payments
														</Text>
													</Td>
												);
											if (column.type === "method") return <Td key="method" />;
											if (column.type === "status")
												return (
													<Td key="status">
														{saved?.status ? (
															<Badge colorScheme={STATUS_COLOR_SCHEMES[saved.status] || "gray"} variant="subtle" fontWeight="normal">
																{saved.status}
																{saved.date && moment(saved.date).isValid() ? ` · ${moment(saved.date).format(fullDateFormat)}` : ""}
															</Badge>
														) : (
															<Text fontWeight="normal" color={mutedColor}>
																-
															</Text>
														)}
													</Td>
												);

											return currencies.map((currency) => {
												if (column.key === "rent") {
													const isOwnerCurrency = currency === ownerCurrency;
													return (
														<Td key={`rent-${currency}`} isNumeric>
															{isOwnerCurrency ? (
																<Badge colorScheme={getRentColorScheme(totals.rent[currency], saved)} variant="subtle" fontWeight="normal">
																	{formatAmount(totals.rent[currency])}
																</Badge>
															) : (
																<Text>{formatAmount(totals.rent[currency])}</Text>
															)}
														</Td>
													);
												}
												if (column.key === "subtotal") {
													const net = getDisplayedMonthNet(saved, totals.net, currency);
													const diverges = net.useSaved && Math.abs(net.primary - net.live) > 0.009;
													return (
														<Td key={`subtotal-${currency}`} isNumeric>
															<Flex alignItems="center" justifyContent="flex-end" gap={1}>
																<Text>{formatAmount(net.primary)}</Text>
																{diverges && (
																	<Tooltip label={`Finalized ${formatAmount(net.primary)} but payments now total ${formatAmount(net.live)}`}>
																		<Box as={WarningTwoIcon} color="orange.400" boxSize={3} />
																	</Tooltip>
																)}
															</Flex>
														</Td>
													);
												}
												return (
													<Td key={`${column.key}-${currency}`} isNumeric>
														{formatAmount(monthValue(totals, column.key, currency))}
													</Td>
												);
											});
										})}
									</Tr>

									{isExpanded &&
										propertyRows.map((row, rowIndex) => {
											const bg = rowIndex % 2 === 0 ? rowBg : stripeBg;
											return (
												<Tr key={row.propertyId || rowIndex} bg={bg}>
													{columns.map((column) => {
														if (column.type === "month") return <Td key="month" {...stickyMonth} bg={bg} />;
														if (column.type === "address")
															return (
																<Td key="address" {...stickyAddress} bg={bg} maxW="14rem">
																	<Tooltip label={row.address}>
																		<Text
																			isTruncated
																			textDecoration={onPropertyClick ? "underline" : undefined}
																			cursor={onPropertyClick ? "pointer" : "default"}
																			color={row.hasActivity ? undefined : mutedColor}
																			onClick={() => row.propertyId && onPropertyClick?.(row.propertyId)}
																		>
																			{row.shortAddress || row.address}
																		</Text>
																	</Tooltip>
																</Td>
															);
														if (column.type === "method")
															return (
																<Td key="method" fontSize="xs" color={mutedColor}>
																	{(row.rent.paymentMethod || []).join(", ")}
																</Td>
															);
														if (column.type === "status") return <Td key="status" />;

														return currencies.map((currency) => {
															const value = propertyValue(row, column.key, currency);
															const tooltipLabel =
																column.key === "rent"
																	? (row.rent.description || []).join("\n")
																	: column.key === "other"
																		? (row.otherDescription || []).join("\n")
																		: "";
															const cell = (
																<MoneyText
																	value={value}
																	colorNegative={column.key === "subtotal"}
																	fontWeight={column.key === "subtotal" ? "semibold" : undefined}
																/>
															);
															return (
																<Td key={`${column.key}-${currency}`} isNumeric>
																	{tooltipLabel ? (
																		<Tooltip label={tooltipLabel} whiteSpace="pre-line">
																			<Box as="span">{cell}</Box>
																		</Tooltip>
																	) : (
																		cell
																	)}
																</Td>
															);
														});
													})}
												</Tr>
											);
										})}

									{isExpanded && propertyRows.length === 0 && (
										<Tr bg={rowBg}>
											<Td {...stickyMonth} bg={rowBg} />
											<Td {...stickyAddress} bg={rowBg} colSpan={columns.length}>
												<Text fontSize="xs" color={mutedColor}>
													No payments recorded this month.
												</Text>
											</Td>
										</Tr>
									)}
								</Fragment>
							);
						})}
					</Tbody>
				</Table>
			</TableContainer>
		</Box>
	);
}
